import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence, type PanInfo } from "framer-motion";
import { getActiveTemplateColumns, getEditableTemplateIdSet } from "@/services/templates";
import { useCurrency } from "@/hooks/useCurrency";
import type { WizardData } from "@/pages/CreateCard";
import { Sparkles, Info, ChevronLeft, ChevronRight, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  data: WizardData;
  updateData: (u: Partial<WizardData>) => void;
}

interface Template {
  id: string;
  name: string;
  image_url: string;
  occasion: string;
  occasions: string[] | null;
  price: number | null;
}

// Pricing filter — sits alongside the occasion tabs so users can
// narrow to free templates or paid ones without re-scrolling the
// grid.
type PriceFilter = "all" | "free" | "paid";

const StepDesign = ({ data, updateData }: Props) => {
  const { t } = useTranslation();
  const currency = useCurrency();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [editableIds, setEditableIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(data.occasion || "all");
  const [priceFilter, setPriceFilter] = useState<PriceFilter>("all");

  // Full-size preview state. `previewIndex` points into `filtered`
  // (not `templates`) so prev/next navigates through the visible
  // set the user is currently browsing.
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  // Once the organiser has picked a template we collapse the whole
  // grid down to a large preview of just that card, so the step
  // reads as "here's the card you chose" rather than forcing them
  // to rescan the catalogue. They can hop back to the grid any
  // time via the Change button.
  const [isChanging, setIsChanging] = useState(!data.templateId);

  const categoryTabs = [
    { label: t("wizard.design.all"), value: "all" },
    { label: t("occasions.farewell"), value: "farewell" },
    { label: t("occasions.teacher"), value: "teacher" },
    { label: t("occasions.birthday"), value: "birthday" },
    { label: t("occasions.baby"), value: "baby" },
    { label: t("occasions.wedding"), value: "wedding" },
    { label: t("occasions.thankYou"), value: "thank-you" },
  ];

  const priceFilters: { label: string; value: PriceFilter }[] = [
    { label: t("wizard.design.priceAll", { defaultValue: "All" }), value: "all" },
    { label: t("wizard.design.priceFree", { defaultValue: "Free" }), value: "free" },
    { label: t("wizard.design.pricePaid", { defaultValue: "Paid" }), value: "paid" },
  ];

  useEffect(() => {
    const fetchAll = async () => {
      const [listResult, editableSet] = await Promise.all([
        getActiveTemplateColumns(),
        getEditableTemplateIdSet(),
      ]);
      setTemplates(listResult.data as any);
      setEditableIds(editableSet);
      setLoading(false);
    };
    fetchAll();
  }, []);

  const selectedTemplate = useMemo(
    () => (data.templateId ? templates.find((t) => t.id === data.templateId) ?? null : null),
    [templates, data.templateId],
  );
  const selectedIsFree = selectedTemplate
    ? !selectedTemplate.price || selectedTemplate.price <= 0
    : true;
  const selectedIsEditable = selectedTemplate ? editableIds.has(selectedTemplate.id) : false;
  const showingSelection = !isChanging && !!data.templateId;

  const filtered = useMemo(() => {
    return templates.filter((tmpl) => {
      const isFree = !tmpl.price || tmpl.price <= 0;
      // Multi-occasion templates set `occasions` as the source of truth
      // and keep the legacy singular `occasion` in sync with the first
      // entry; match on either so templates tagged for the picked
      // occasion as a secondary still show up. Templates without any
      // occasion set are treated as wildcards and shown under every
      // filter.
      if (activeTab !== "all") {
        const hasAnyOccasion =
          !!tmpl.occasion ||
          (Array.isArray(tmpl.occasions) && tmpl.occasions.length > 0);
        const matches =
          !hasAnyOccasion ||
          tmpl.occasion === activeTab ||
          (Array.isArray(tmpl.occasions) && tmpl.occasions.includes(activeTab));
        if (!matches) return false;
      }
      if (priceFilter === "free" && !isFree) return false;
      if (priceFilter === "paid" && isFree) return false;
      return true;
    });
  }, [templates, activeTab, priceFilter]);

  // Looping step helpers — the preview wraps around in both
  // directions so the deck never hits a dead end.
  const stepNext = () =>
    setPreviewIndex((idx) =>
      idx == null || filtered.length === 0 ? idx ?? null : (idx + 1) % filtered.length,
    );
  const stepPrev = () =>
    setPreviewIndex((idx) =>
      idx == null || filtered.length === 0
        ? idx ?? null
        : (idx - 1 + filtered.length) % filtered.length,
    );

  // Keyboard navigation inside the lightbox — arrow keys step
  // through the filtered list with wrap-around, Escape closes.
  useEffect(() => {
    if (previewIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPreviewIndex(null);
      if (e.key === "ArrowLeft") stepPrev();
      if (e.key === "ArrowRight") stepNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [previewIndex, filtered.length]);

  // Swipe gesture for the stacked-deck carousel. Tuned to feel
  // responsive: a short drag (>40px) or a quick flick advances.
  // Swipe left → next card, swipe right → previous card, both loop.
  const handleDragEnd = (_: unknown, info: PanInfo) => {
    const distance = 40;
    const velocity = 250;
    if (previewIndex == null) return;
    const goNext =
      info.offset.x < -distance || info.velocity.x < -velocity;
    const goPrev =
      info.offset.x > distance || info.velocity.x > velocity;
    if (goNext) stepNext();
    else if (goPrev) stepPrev();
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-foreground font-serif mb-3">
        {t("wizard.design.title")}
      </h2>

      {showingSelection ? (
        // Selected-template view — a single large card with the
        // chosen design, its metadata, and a Change button that
        // flips back to the grid. When the template list hasn't
        // loaded yet (draft resume) we show a skeleton instead of
        // an empty column.
        loading || !selectedTemplate ? (
          <div className="mx-auto w-full max-w-sm">
            <div className="aspect-[3/4] rounded-xl bg-muted animate-pulse" />
            <div className="mt-3 h-3 w-2/3 bg-muted animate-pulse rounded" />
            <div className="mt-4 h-11 bg-muted animate-pulse rounded-full" />
          </div>
        ) : (
          <div className="mx-auto w-full max-w-sm flex flex-col gap-4">
            <div className="relative w-full aspect-[3/4] rounded-lg overflow-hidden shadow-md bg-white border border-border">
              <img
                src={data.customCoverUrl || selectedTemplate.image_url}
                alt={selectedTemplate.name}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => setIsChanging(true)}
                className="absolute top-2 end-2 rounded-full bg-background/95 backdrop-blur-sm border border-border px-3 py-1.5 text-xs font-semibold text-foreground shadow-sm hover:bg-muted transition-colors"
              >
                {t("wizard.design.changeTemplate", { defaultValue: "Change" })}
              </button>
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <p
                  className="text-sm font-semibold text-foreground truncate"
                  title={selectedTemplate.name}
                >
                  {selectedTemplate.name}
                </p>
                {selectedIsEditable && (
                  <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                    <Sparkles size={9} />
                    {t("wizard.design.editable", { defaultValue: "Editable" })}
                  </span>
                )}
              </div>
              <span
                className={cn(
                  "shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded",
                  selectedIsFree
                    ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                    : "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
                )}
              >
                {selectedIsFree
                  ? t("wizard.design.free", { defaultValue: "Free" })
                  : `${currency} ${selectedTemplate.price}`}
              </span>
            </div>
          </div>
        )
      ) : (
        <>
      {/* Explainer — "Editable" tag means the organiser can tweak
          the design later from the card manage page. */}
      <div className="mb-5 flex items-start gap-2 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2.5 text-xs text-foreground/80">
        <Info size={14} className="text-primary shrink-0 mt-0.5" />
        <p>
          {t("wizard.design.editableNote", {
            defaultValue:
              "Templates marked “Editable” can be customized later from the card manage page.",
          })}
        </p>
      </div>

      {/* Category tabs (occasion) */}
      <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-2 -mx-1 px-1">
        {categoryTabs.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setActiveTab(tab.value)}
            className={`whitespace-nowrap text-xs font-medium px-4 py-2 rounded-full border transition-colors flex-shrink-0 ${
              activeTab === tab.value
                ? "bg-primary/10 border-primary text-primary"
                : "bg-background border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Price filter — All / Free / Paid. Segmented pill style so
          it's visually distinct from the occasion tabs above. */}
      <div className="flex items-center gap-1 mb-6 bg-muted/50 rounded-full p-1 w-fit">
        {priceFilters.map((f) => (
          <button
            key={f.value}
            onClick={() => setPriceFilter(f.value)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${
              priceFilter === f.value
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Inline stacked-deck carousel — replaces the grid so the
          organiser browses templates as a swipeable deck of cards
          right inside the wizard. Tapping the active card selects
          it; swipe / arrows step through the filtered set. */}
      {loading ? (
        <div className="mx-auto w-full max-w-sm">
          <div className="aspect-[3/4] rounded-xl bg-muted animate-pulse" />
          <div className="mt-3 h-3 w-2/3 bg-muted animate-pulse rounded mx-auto" />
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-12">
          {t("wizard.design.noTemplates")}
        </p>
      ) : (
        <InlineStack
          filtered={filtered}
          editableIds={editableIds}
          data={data}
          updateData={updateData}
          setIsChanging={setIsChanging}
          currency={currency}
          t={t}
        />
      )}
        </>
      )}

      {/* Full-size preview lightbox. When open, renders the
          filtered list as a swipeable horizontal carousel — each
          slide fills the viewport width so the user can drag or
          flick left/right to browse, the same way a product-detail
          gallery works on mobile commerce apps. The prev/next
          buttons and arrow keys are just shortcuts into the same
          scroll behaviour. */}
      {previewIndex !== null && filtered.length > 0 && (
        <div
          className="fixed inset-0 z-[150] bg-black/80 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          {/* Close */}
          <button
            type="button"
            aria-label={t("common.close", { defaultValue: "Close" })}
            onClick={() => setPreviewIndex(null)}
            className="absolute top-4 end-4 z-10 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
          >
            <X size={20} />
          </button>

          {/* Prev — hidden on small screens where swipe is natural.
              Loops back to the last card when pressed on the first. */}
          <button
            type="button"
            aria-label={t("common.previous", { defaultValue: "Previous" })}
            onClick={stepPrev}
            className="hidden sm:flex absolute start-4 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center transition-colors"
          >
            <ChevronLeft size={24} />
          </button>

          {/* Stacked-deck carousel — cards are absolutely positioned
              and overlap so the neighbouring cards peek from behind
              on both sides, like a deck of cards. The active card
              sits on top; swipe / drag advances and loops. */}
          <div className="absolute inset-0 flex items-start justify-center pt-16 pb-28 px-4">
            <div
              className="relative w-full"
              style={{
                maxWidth: "min(78vw, calc((100vh - 240px) * 0.75))",
                aspectRatio: "3 / 4",
              }}
            >
              {filtered.map((tmpl, slideIdx) => {
                const n = filtered.length;
                // Wrap the raw distance onto a ring so that neighbours
                // across the loop boundary render as ±1 instead of
                // (n-1), keeping the peek continuous when navigating
                // from the last card back to the first.
                const raw = ((slideIdx - (previewIndex ?? 0)) % n + n) % n;
                const offset = raw > n / 2 ? raw - n : raw;
                const absDist = Math.abs(offset);
                if (absDist > 2) return null; // cull off-screen cards
                const isActive = offset === 0;
                // Symmetric peek: trailing cards (offset > 0) lean
                // right, leading cards (offset < 0) lean left. Both
                // are scaled down and nudged down behind the active.
                const scale = isActive ? 1 : 1 - Math.min(absDist, 2) * 0.06;
                const translateX =
                  offset > 0
                    ? Math.min(offset, 2) * 22
                    : offset < 0
                    ? Math.max(offset, -2) * 22
                    : 0;
                const translateY = Math.min(absDist, 2) * 14;
                const opacity = 1;
                const zIndex = 50 - absDist;
                return (
                  <motion.div
                    key={tmpl.id}
                    className="absolute inset-0 rounded-2xl overflow-hidden bg-white border border-border/30"
                    style={{
                      boxShadow: isActive
                        ? "0 30px 60px -15px rgba(0,0,0,0.55), 0 10px 25px -10px rgba(0,0,0,0.4)"
                        : "0 16px 30px -12px rgba(0,0,0,0.45)",
                      zIndex,
                      transformOrigin: "center center",
                      cursor: isActive ? "grab" : "default",
                    }}
                    animate={{
                      x: translateX,
                      y: translateY,
                      scale,
                      opacity,
                    }}
                    transition={{ type: "spring", stiffness: 380, damping: 32 }}
                    drag={isActive ? "x" : false}
                    dragConstraints={{ left: 0, right: 0 }}
                    dragElastic={1}
                    dragMomentum={false}
                    onDragEnd={handleDragEnd}
                    whileDrag={{ cursor: "grabbing" }}
                  >
                    <img
                      src={tmpl.image_url}
                      alt={tmpl.name}
                      loading="lazy"
                      draggable={false}
                      className="w-full h-full object-cover select-none pointer-events-none"
                    />
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Active card metadata + CTA, pinned above the bottom
              indicator. Lives outside the stack so it doesn't get
              clipped by the card's overflow-hidden. */}
          {(() => {
            const tmpl = filtered[previewIndex];
            if (!tmpl) return null;
            const isFree = !tmpl.price || tmpl.price <= 0;
            const isEditable = editableIds.has(tmpl.id);
            const isSelected = data.templateId === tmpl.id;
            return (
              <div className="absolute bottom-16 inset-x-0 z-20 px-6 flex flex-col items-center gap-3">
                <div className="w-full max-w-sm flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0" style={{ perspective: 600 }}>
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.p
                        key={tmpl.id}
                        initial={{ rotateX: -90, opacity: 0 }}
                        animate={{ rotateX: 0, opacity: 1 }}
                        exit={{ rotateX: 90, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="text-sm font-semibold text-white truncate"
                        title={tmpl.name}
                        style={{ transformOrigin: "center", backfaceVisibility: "hidden" }}
                      >
                        {tmpl.name}
                      </motion.p>
                    </AnimatePresence>
                    {isEditable && (
                      <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                        <Sparkles size={9} />
                        {t("wizard.design.editable", { defaultValue: "Editable" })}
                      </span>
                    )}
                  </div>
                  <span
                    className={cn(
                      "shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded",
                      isFree
                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                        : "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
                    )}
                  >
                    {isFree
                      ? t("wizard.design.free", { defaultValue: "Free" })
                      : `${currency} ${tmpl.price}`}
                  </span>
                </div>
                {!isSelected && (
                  <button
                    type="button"
                    onClick={() => {
                      updateData({
                        templateId: tmpl.id,
                        templateUrl: tmpl.image_url,
                        customCoverUrl: "",
                        customDesignJson: null,
                      });
                      setPreviewIndex(null);
                      setIsChanging(false);
                    }}
                    className="w-full max-w-sm rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
                  >
                    {t("wizard.design.selectThis", { defaultValue: "Use this template" })}
                  </button>
                )}
              </div>
            );
          })()}

          {/* Position indicator pinned to the bottom — always shows
              the "X / N" counter, plus a compact dot row when the
              filtered list is short enough for it to stay readable. */}
          <div className="absolute bottom-4 inset-x-0 z-10 flex flex-col items-center gap-2 pointer-events-none">
            {filtered.length <= 10 && (
              <div className="flex items-center gap-1.5">
                {filtered.map((_, i) => (
                  <span
                    key={i}
                    className={cn(
                      "rounded-full transition-all",
                      i === previewIndex ? "h-1.5 w-4 bg-white" : "h-1.5 w-1.5 bg-white/40",
                    )}
                  />
                ))}
              </div>
            )}
            <span className="text-[11px] text-white/70 tabular-nums">
              {previewIndex + 1} / {filtered.length}
            </span>
          </div>

          {/* Next — loops to the first card when pressed on the last. */}
          <button
            type="button"
            aria-label={t("common.next", { defaultValue: "Next" })}
            onClick={stepNext}
            className="hidden sm:flex absolute end-4 top-1/2 -translate-y-1/2 z-10 h-12 w-12 rounded-full bg-white/10 hover:bg-white/20 text-white items-center justify-center transition-colors"
          >
            <ChevronRight size={24} />
          </button>
        </div>
      )}
    </div>
  );
};

interface InlineStackProps {
  filtered: Template[];
  editableIds: Set<string>;
  data: WizardData;
  updateData: (u: Partial<WizardData>) => void;
  setIsChanging: (v: boolean) => void;
  currency: string;
  t: (key: string, opts?: any) => string;
}

const InlineStack = ({
  filtered,
  editableIds,
  data,
  updateData,
  setIsChanging,
  currency,
  t,
}: InlineStackProps) => {
  const [index, setIndex] = useState(0);

  // Reset index when filtered set changes shape so we don't land on
  // an out-of-range card after switching tabs.
  useEffect(() => {
    setIndex((i) => (i >= filtered.length ? 0 : i));
  }, [filtered.length]);

  const stepNext = () =>
    setIndex((i) => (filtered.length === 0 ? i : (i + 1) % filtered.length));
  const stepPrev = () =>
    setIndex((i) =>
      filtered.length === 0 ? i : (i - 1 + filtered.length) % filtered.length,
    );

  const handleDragEnd = (_: unknown, info: PanInfo) => {
    const distance = 40;
    const velocity = 250;
    const goNext = info.offset.x < -distance || info.velocity.x < -velocity;
    const goPrev = info.offset.x > distance || info.velocity.x > velocity;
    if (goNext) stepNext();
    else if (goPrev) stepPrev();
  };

  const active = filtered[index];
  if (!active) return null;
  const isFree = !active.price || active.price <= 0;
  const isEditable = editableIds.has(active.id);
  const isSelected = data.templateId === active.id;

  return (
    <div className="flex flex-col items-center gap-5">
      <div className="relative w-full flex items-center justify-center">
        <button
          type="button"
          aria-label={t("common.previous", { defaultValue: "Previous" })}
          onClick={stepPrev}
          className="hidden sm:flex absolute start-0 top-1/2 -translate-y-1/2 z-30 h-10 w-10 rounded-full bg-background border border-border text-foreground items-center justify-center shadow-sm hover:bg-muted transition-colors"
        >
          <ChevronLeft size={20} />
        </button>

        <div
          className="relative w-full"
          style={{
            maxWidth: "min(78vw, 320px)",
            aspectRatio: "3 / 4",
          }}
        >
          {filtered.map((tmpl, slideIdx) => {
            const n = filtered.length;
            const raw = ((slideIdx - index) % n + n) % n;
            const offset = raw > n / 2 ? raw - n : raw;
            const absDist = Math.abs(offset);
            if (absDist > 2) return null;
            const isActive = offset === 0;
            const scale = isActive ? 1 : 1 - Math.min(absDist, 2) * 0.06;
            const translateX =
              offset > 0
                ? Math.min(offset, 2) * 22
                : offset < 0
                ? Math.max(offset, -2) * 22
                : 0;
            const translateY = Math.min(absDist, 2) * 14;
            const zIndex = 50 - absDist;
            const tmplSelected = data.templateId === tmpl.id;
            return (
              <motion.div
                key={tmpl.id}
                className={cn(
                  "absolute inset-0 rounded-2xl overflow-hidden bg-white border-2",
                  isActive && tmplSelected
                    ? "border-primary"
                    : "border-border/30",
                )}
                style={{
                  boxShadow: isActive
                    ? "0 30px 60px -15px hsl(var(--foreground) / 0.35), 0 10px 25px -10px hsl(var(--foreground) / 0.25)"
                    : "0 16px 30px -12px hsl(var(--foreground) / 0.25)",
                  zIndex,
                  transformOrigin: "center center",
                  cursor: isActive ? "grab" : "default",
                }}
                animate={{ x: translateX, y: translateY, scale }}
                transition={{ type: "spring", stiffness: 380, damping: 32 }}
                drag={isActive ? "x" : false}
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={1}
                dragMomentum={false}
                onDragEnd={handleDragEnd}
                whileDrag={{ cursor: "grabbing" }}
              >
                <img
                  src={tmpl.image_url}
                  alt={tmpl.name}
                  loading="lazy"
                  draggable={false}
                  className="w-full h-full object-cover select-none pointer-events-none"
                />
              </motion.div>
            );
          })}
        </div>

        <button
          type="button"
          aria-label={t("common.next", { defaultValue: "Next" })}
          onClick={stepNext}
          className="hidden sm:flex absolute end-0 top-1/2 -translate-y-1/2 z-30 h-10 w-10 rounded-full bg-background border border-border text-foreground items-center justify-center shadow-sm hover:bg-muted transition-colors"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Active card metadata */}
      <div className="w-full max-w-sm flex items-center justify-between gap-2 px-2">
        <div className="flex items-center gap-2 min-w-0">
          <p
            className="text-sm font-semibold text-foreground truncate"
            title={active.name}
          >
            {active.name}
          </p>
          {isEditable && (
            <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-primary/10 text-primary">
              <Sparkles size={9} />
              {t("wizard.design.editable", { defaultValue: "Editable" })}
            </span>
          )}
        </div>
        <span
          className={cn(
            "shrink-0 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded",
            isFree
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
              : "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
          )}
        >
          {isFree
            ? t("wizard.design.free", { defaultValue: "Free" })
            : `${currency} ${active.price}`}
        </span>
      </div>

      <button
        type="button"
        onClick={() => {
          if (isSelected) {
            setIsChanging(false);
            return;
          }
          updateData({
            templateId: active.id,
            templateUrl: active.image_url,
            customCoverUrl: "",
            customDesignJson: null,
          });
          setIsChanging(false);
        }}
        className="w-full max-w-sm rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
      >
        {isSelected
          ? t("wizard.design.selected", { defaultValue: "Selected" })
          : t("wizard.design.selectThis", { defaultValue: "Use this template" })}
      </button>

      {/* Position indicator */}
      <div className="flex flex-col items-center gap-1.5">
        {filtered.length <= 10 && (
          <div className="flex items-center gap-1.5">
            {filtered.map((_, i) => (
              <span
                key={i}
                className={cn(
                  "rounded-full transition-all",
                  i === index ? "h-1.5 w-4 bg-foreground" : "h-1.5 w-1.5 bg-muted-foreground/40",
                )}
              />
            ))}
          </div>
        )}
        <span className="text-[11px] text-muted-foreground tabular-nums">
          {index + 1} / {filtered.length}
        </span>
      </div>
    </div>
  );
};

export default StepDesign;
